export type SwitchFeature = {
  type: 'switch';
  configKey: string;
  labelKey: string;
  descKey: string;
  getValue: (config: Record<string, any> | undefined) => boolean; // eslint-disable-line no-unused-vars
};

export type NumberFeature = {
  type: 'number';
  configKey: string;
  labelKey: string;
  descKey: string;
  getValue: (config: Record<string, any> | undefined) => number; // eslint-disable-line no-unused-vars
  min: number;
  max: number;
};

type Feature = SwitchFeature | NumberFeature;

export type FeatureGroup = {
  key: string;
  labelKey: string;
  icon: string;
  color: string;
  features: Feature[];
};

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    key: 'general',
    labelKey: 'SocietyFeatureGroup_General',
    icon: 'ni-setting',
    color: 'primary',
    features: [
      {
        type: 'number',
        configKey: 'decimalScale',
        labelKey: 'SocietyFeature_DecimalScale',
        descKey: 'SocietyFeature_DecimalScale_Desc',
        getValue: (c) => c?.decimalScale ?? 2,
        min: 0,
        max: 8,
      },
      {
        type: 'switch',
        configKey: 'hasWhiteBranding',
        labelKey: 'SocietyFeature_HasWhiteBranding',
        descKey: 'SocietyFeature_HasWhiteBranding_Desc',
        getValue: (c) => c?.hasWhiteBranding ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasLoginRedirect',
        labelKey: 'SocietyFeature_HasLoginRedirect',
        descKey: 'SocietyFeature_HasLoginRedirect_Desc',
        getValue: (c) => c?.hasLoginRedirect ?? false,
      },
    ],
  },
  {
    key: 'partners',
    labelKey: 'SocietyFeatureGroup_Partners',
    icon: 'ni-users',
    color: 'success',
    features: [
      {
        type: 'switch',
        configKey: 'hasOldPartnersOn',
        labelKey: 'SocietyFeature_HasOldPartnersOn',
        descKey: 'SocietyFeature_HasOldPartnersOn_Desc',
        getValue: (c) => c?.hasOldPartnersOn ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasPartnerSharesRangeDisabled',
        labelKey: 'SocietyFeature_HasPartnerSharesRangeDisabled',
        descKey: 'SocietyFeature_HasPartnerSharesRangeDisabled_Desc',
        getValue: (c) => c?.hasPartnerSharesRangeDisabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasPartnerDocumentsTabDisabled',
        labelKey: 'SocietyFeature_HasPartnerDocumentsTabDisabled',
        descKey: 'SocietyFeature_HasPartnerDocumentsTabDisabled_Desc',
        getValue: (c) => c?.hasPartnerDocumentsTabDisabled ?? false,
      },
    ],
  },
  {
    key: 'beneficiaries',
    labelKey: 'SocietyFeatureGroup_Beneficiaries',
    icon: 'ni-gift',
    color: 'warning',
    features: [
      {
        type: 'switch',
        configKey: 'hasBeneficiaryStrikeDisabled',
        labelKey: 'SocietyFeature_HasBeneficiaryStrikeDisabled',
        descKey: 'SocietyFeature_HasBeneficiaryStrikeDisabled_Desc',
        getValue: (c) => c?.hasBeneficiaryStrikeDisabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasBeneficiaryPercentDisabled',
        labelKey: 'SocietyFeature_HasBeneficiaryPercentDisabled',
        descKey: 'SocietyFeature_HasBeneficiaryPercentDisabled_Desc',
        getValue: (c) => c?.hasBeneficiaryPercentDisabled ?? false,
      },
    ],
  },
  {
    key: 'functionalities',
    labelKey: 'SocietyFeatureGroup_Functionalities',
    icon: 'ni-layers',
    color: 'info',
    features: [
      {
        type: 'switch',
        configKey: 'hasIncentiveChart',
        labelKey: 'SocietyFeature_HasIncentiveChart',
        descKey: 'SocietyFeature_HasIncentiveChart_Desc',
        getValue: (c) => c?.hasIncentiveChart ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasCouncilsV2Enabled',
        labelKey: 'SocietyFeature_HasCouncilsV2Enabled',
        descKey: 'SocietyFeature_HasCouncilsV2Enabled_Desc',
        getValue: (c) => c?.hasCouncilsV2Enabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasSEPAOption',
        labelKey: 'SocietyFeature_HasSEPAOption',
        descKey: 'SocietyFeature_HasSEPAOption_Desc',
        getValue: (c) => c?.hasSEPAOption ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasVideoOnBoardsEnabled',
        labelKey: 'SocietyFeature_HasVideoOnBoardsEnabled',
        descKey: 'SocietyFeature_HasVideoOnBoardsEnabled_Desc',
        getValue: (c) => c?.hasVideoOnBoardsEnabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasThirdPartyIntegrations',
        labelKey: 'SocietyFeature_HasThirdPartyIntegrations',
        descKey: 'SocietyFeature_HasThirdPartyIntegrations_Desc',
        getValue: (c) => c?.hasThirdPartyIntegrations ?? false,
      },
      {
        type: 'switch',
        configKey: 'hasAnalyticsPageTracking',
        labelKey: 'SocietyFeature_HasAnalyticsPageTracking',
        descKey: 'SocietyFeature_HasAnalyticsPageTracking_Desc',
        getValue: (c) => c?.hasAnalyticsPageTracking ?? false,
      },
    ],
  },
  {
    key: 'modules',
    labelKey: 'SocietyFeatureGroup_Modules',
    icon: 'ni-package',
    color: 'purple',
    features: [
      {
        type: 'switch',
        configKey: 'sharesTitles.enabled',
        labelKey: 'SocietyFeature_SharesTitles',
        descKey: 'SocietyFeature_SharesTitles_Desc',
        getValue: (c) => c?.sharesTitles?.enabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'equityMarket.enabled',
        labelKey: 'SocietyFeature_EquityMarket',
        descKey: 'SocietyFeature_EquityMarket_Desc',
        getValue: (c) => c?.equityMarket?.enabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'notifications.enabled',
        labelKey: 'SocietyFeature_Notifications',
        descKey: 'SocietyFeature_Notifications_Desc',
        getValue: (c) => c?.notifications?.enabled ?? false,
      },
      {
        type: 'switch',
        configKey: 'aiPlan.enabled',
        labelKey: 'SocietyFeature_AiPlan',
        descKey: 'SocietyFeature_AiPlan_Desc',
        getValue: (c) => c?.aiPlan?.enabled ?? false,
      },
    ],
  },
];
